import { Navigate, Outlet } from 'react-router';
import type { ActorType } from 'shared';
import { useActor } from '../../lib/capabilities.js';

/**
 * Keeps owners in the owner portal, residents in the resident portal, and
 * vendors in the vendor portal.
 *
 * Not a security control — `RequireAuth` already established that this is a portal
 * actor, and RLS decides every row either of them can see. This is about coherence:
 * an owner who lands on the resident screen would get a page built around "your
 * home" and "what you owe", filled with whatever their own policies return, which is
 * a confusing page rather than a leaking one.
 *
 * VENDOR fits this same guard rather than needing its own route tree the
 * way APPLICANT did (see RequireApplicant) — a vendor's portal is entirely
 * read-only plus one Connect-enrollment write, the same shape OWNER/TENANT
 * already have, not a new write-capable actor that breaks the pattern.
 *
 * Redirecting to `/portal` rather than rendering an error, because there is a
 * correct destination and the user did not do anything wrong to get here — a stale
 * bookmark from before the portals were split is the likely cause.
 */
export function RequirePortalActor({
  actorType,
}: {
  actorType: Extract<ActorType, 'OWNER' | 'TENANT' | 'VENDOR'>;
}) {
  const actor = useActor();

  if (!actor) return null;
  if (actor.actorType !== actorType) return <Navigate to="/portal" replace />;

  return <Outlet />;
}
